import React, { createContext, useCallback, useContext, useEffect, useRef, useState } from "react"

const ResearchContext = createContext(null)

export function ResearchProvider({ children }) {
  const [session,          setSession]          = useState(null)
  const [findings,         setFindings]         = useState([])
  const [narrativeLog,     setNarrativeLog]     = useState([])
  const [phase,            setPhase]            = useState(null)
  const [vaultScan,        setVaultScan]        = useState({ progress: 0, total: 0, foundFiles: [], ready: false })
  const [consolidate,      setConsolidate]      = useState({ detail: "", current: 0, total: 3, step: "" })
  const [gaps,             setGaps]             = useState([])
  const [vaultSuggestion,  setVaultSuggestion]  = useState(null)
  const [knowledgeArticle, setKnowledgeArticle] = useState(null)
  const [library,          setLibrary]          = useState([])
  const [libraryLoading,   setLibraryLoading]   = useState(false)
  const [researchSettings, setResearchSettings] = useState(null)

  const esRef             = useRef(null)
  const reconnectDelayRef = useRef(3000)
  const reconnectTimerRef = useRef(null)  // BUG FIX #1: track timer for unmount cleanup
  const stoppedRef        = useRef(false) // explicit stop must not trigger a reconnect

  const _connectStream = useCallback(() => {
    if (esRef.current) esRef.current.close()
    stoppedRef.current = false
    const es = new EventSource("/api/research/stream/")
    esRef.current = es

    es.onmessage = (e) => {
      reconnectDelayRef.current = 3000
      try {
        const msg = JSON.parse(e.data)
        if (msg.type === "status") {
          setSession((prev) => ({ ...(prev ?? {}), ...msg }))
          if (msg.phase != null) setPhase(msg.phase)
        } else if (msg.type === "finding") {
          setFindings((prev) => [msg, ...prev].slice(0, 200))
          setSession((prev) => prev
            ? { ...prev, gaps_done: msg.gaps_done ?? prev.gaps_done, gaps_remaining: msg.gaps_remaining ?? prev.gaps_remaining }
            : prev
          )
        } else if (msg.type === "narrative") {
          if (msg.replace) {
            setNarrativeLog((prev) => {
              if (prev.length === 0) return [msg.text]
              return [...prev.slice(0, -1), msg.text].slice(-30)
            })
          } else {
            setNarrativeLog((prev) => [...prev, msg.text].slice(-30))
          }
        } else if (msg.type === "findings_history") {
          // BUG FIX #5: take newest 200 first, then reverse for newest-first order
          setFindings(msg.findings.slice(-200).reverse())
        } else if (msg.type === "phase_change") {
          setPhase(msg.phase)
        } else if (msg.type === "gaps_identified") {
          setGaps(msg.gaps ?? [])
        } else if (msg.type === "vault_scan_progress") {
          setVaultScan((prev) => ({
            ...prev,
            progress: msg.current,
            total: msg.total,
            foundFiles: msg.relevant ? [...prev.foundFiles, msg.file] : prev.foundFiles,
          }))
        } else if (msg.type === "vault_context_ready") {
          setVaultScan((prev) => ({ ...prev, ready: true, foundFiles: msg.files ?? prev.foundFiles }))
        } else if (msg.type === "consolidate_progress") {
          setConsolidate({ detail: msg.detail ?? "", current: msg.current ?? 0, total: msg.total ?? 3, step: msg.step ?? "" })
        } else if (msg.type === "vault_suggestion") {
          setVaultSuggestion({
            title: msg.title,
            content: msg.content,
            fullContent: msg.full_content,
            suggestedPath: msg.suggested_path,
            sourceCount: msg.source_count ?? 0,
          })
        } else if (msg.type === "knowledge_article") {
          setKnowledgeArticle({ title: msg.title, content: msg.content, vaultPath: msg.vault_path, sourceCount: msg.source_count ?? 0 })
        } else if (msg.type === "done") {
          setSession((prev) => prev ? { ...prev, status: "done", active: false } : prev)
          setNarrativeLog((prev) => [...prev, msg.awaiting_save
            ? "Recherche abgeschlossen — warte auf Speicher-Bestätigung."
            : "Recherche abgeschlossen."
          ].slice(-30))
          setPhase(null)
        } else if (msg.type === "idle") {
          setSession(null)
        } else if (msg.type === "error") {
          setSession((prev) => prev ? { ...prev, status: "error", errorMsg: msg.message } : prev)
        }
      } catch {
        // malformed event — ignore
      }
    }

    es.onerror = () => {
      es.close()
      esRef.current = null
      if (stoppedRef.current) return
      const delay = reconnectDelayRef.current
      reconnectDelayRef.current = Math.min(delay * 2, 30000)
      reconnectTimerRef.current = setTimeout(_connectStream, delay)
    }
  }, [])

  useEffect(() => {
    fetch("/api/research/status/")
      .then((r) => r.json())
      .then((data) => {
        if (data.active) {
          setSession(data)
          if (data.phase != null) setPhase(data.phase)
          _connectStream()
        }
      })
      .catch(() => {})
    return () => {
      esRef.current?.close()
      clearTimeout(reconnectTimerRef.current)
    }
  }, [_connectStream])

  useEffect(() => {
    if (session?.status !== "running") return
    const id = setInterval(() => {
      setSession((prev) => {
        if (!prev || prev.status !== "running") return prev
        return { ...prev, elapsed: (prev.elapsed ?? 0) + 1 }
      })
    }, 1000)
    return () => clearInterval(id)
  }, [session?.status])

  const fetchLibrary = useCallback(async () => {
    setLibraryLoading(true)
    try {
      const r = await fetch("/api/research/library/")
      const d = await r.json()
      setLibrary(d.articles ?? [])
    } catch {
      // silent — library is optional
    } finally {
      setLibraryLoading(false)
    }
  }, [])

  const deleteLibraryArticle = useCallback(async (vaultPath) => {
    await fetch("/api/research/library/delete/", {
      method: "DELETE",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ vault_path: vaultPath }),
    })
    await fetchLibrary()
  }, [fetchLibrary])

  // BUG FIX #2: reset state AFTER successful fetch, not before
  const startResearch = useCallback(async (topic, depthLevel, powerLevel, focusHint = "") => {
    const res = await fetch("/api/research/start/", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ topic, depth_level: depthLevel, power_level: powerLevel, focus_hint: focusHint }),
    })
    const data = await res.json()
    if (!data.ok) throw new Error(data.error ?? "Start fehlgeschlagen")

    setFindings([])
    setNarrativeLog([])
    setPhase(null)
    setVaultScan({ progress: 0, total: 0, foundFiles: [], ready: false })
    setConsolidate({ detail: "", current: 0, total: 3, step: "" })
    setGaps([])
    setVaultSuggestion(null)
    setKnowledgeArticle(null)

    const depthGaps = { quick: 5, normal: 15, deep: 30 }[depthLevel] ?? 15
    setSession({
      active: true, topic,
      depth_level: depthLevel,
      target_gaps: depthGaps,
      gaps_done: 0,
      gaps_remaining: depthGaps,
      power_level: powerLevel,
      status: "running",
      finding_count: 0,
      saved_count: 0,
      elapsed: 0,
    })
    _connectStream()
  }, [_connectStream])

  // BUG FIX #7: close EventSource on stop to prevent unwanted reconnect loop
  const stopResearch = useCallback(async () => {
    stoppedRef.current = true
    await fetch("/api/research/stop/", { method: "POST" })
    esRef.current?.close()
    esRef.current = null
    clearTimeout(reconnectTimerRef.current)
    reconnectDelayRef.current = 3000
    setSession((prev) => prev ? { ...prev, status: "stopped", active: false } : prev)
    setNarrativeLog((prev) => [...prev, "Recherche gestoppt."].slice(-30))
    setPhase(null)
  }, [])

  const fetchResearchSettings = useCallback(async () => {
    try {
      const r = await fetch("/api/settings/")
      const d = await r.json()
      setResearchSettings({
        model: d.research_model ?? "",
        temperature: d.research_temperature ?? 0.7,
        relevanceThreshold: d.research_relevance_threshold ?? 4,
        searchEngine: d.research_search_engine ?? "auto",
        strategyLearning: d.research_strategy_learning ?? true,
        maxUrls: d.research_max_urls ?? 0,
      })
    } catch {
      // settings panel simply stays hidden if unavailable
    }
  }, [])

  const saveResearchSettings = useCallback(async (s) => {
    const res = await fetch("/api/settings/", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        research_model: s.model,
        research_temperature: s.temperature,
        research_relevance_threshold: s.relevanceThreshold,
        research_search_engine: s.searchEngine,
        research_strategy_learning: s.strategyLearning,
        research_max_urls: s.maxUrls,
      }),
    })
    if (!res.ok) throw new Error("Settings speichern fehlgeschlagen")
    setResearchSettings(s)
  }, [])

  const confirmSave = useCallback(async (path) => {
    const res = await fetch("/api/research/save/", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ path }),
    })
    const data = await res.json()
    if (!data.ok) throw new Error(data.error ?? "Speichern fehlgeschlagen")
    setVaultSuggestion(null)
    fetchLibrary()
  }, [fetchLibrary])

  return (
    <ResearchContext.Provider value={{
      session, findings, narrativeLog,
      phase, vaultScan, consolidate, gaps,
      vaultSuggestion, knowledgeArticle,
      library, libraryLoading,
      researchSettings, fetchResearchSettings, saveResearchSettings,
      startResearch, stopResearch, confirmSave,
      fetchLibrary, deleteLibraryArticle,
    }}>
      {children}
    </ResearchContext.Provider>
  )
}

export function useResearch() {
  const ctx = useContext(ResearchContext)
  if (!ctx) throw new Error("useResearch must be used inside ResearchProvider")
  return ctx
}
